/**
 * ── A BUTTON'S LINK HAS TO FOLLOW ITS LABEL, OR SAY IT DIDN'T (2026-09-28, #268) ──────
 *
 * Form Feedback, CHB, 2026-09-11: "There is a scroll down for the Button Label but when
 * another button is selected, the Button URL does not reflect that change. If the URL
 * will not auto change when a different Button Label is selected, then make the Link
 * have a validation rule to prompt the user to update the link."
 *
 * Retested on emailbuilder-dev 2026-09-28 (OLH · Coming Soon · CTA Buttons 2 Up):
 * "Join Interest List¹" → "Get Pre-Qualified¹" left www.lennar.com/new-homes/florida/
 * orlando/ under the button and said nothing.
 *
 * The auto half already exists: an unclaimed CTA link is `ctaUrlFor(label)`, so a tour,
 * interest-list, directions, contact, call or floorplan label re-routes on its own. Two
 * cases fall through, and those are the two this file names:
 *
 *   1. THE LABEL HAS NO DESTINATION THE HUB KNOWS. `ctaUrlFor` has no rule for a
 *      pre-qualification, an offer, an event RSVP, a calendar file, a video, a referral
 *      or Lennar Mortgage, so those labels land on the scope's page — the community or
 *      market page the previous label pointed at. The Hub has no source for those
 *      pages and must not invent one, so it asks for the link.
 *   2. THE REQUESTER TYPED THE LINK, THEN CHANGED THE LABEL. A typed link is theirs
 *      (`__socOff`) and the fill never touches it, which is right — but the label it was
 *      typed for is remembered (`__urlLbl`), and when the label moves on, the link is
 *      flagged for a second look.
 *
 * Both are prompts, not blocks: a campaign page can legitimately take any label.
 */
import { echNameOf } from "./echRegistry";
import { ECH_CTA_PAIRS, socIsOff, type DynBlock } from "./echDyn";

/** labels whose page the Hub cannot fill — the requester has to supply it */
const NEEDS_OWN_LINK = /pre-?qualif|\boffer\b|\brsvp\b|calendar|watch now|learn more|refer a friend|lennar mortgage/i;

/** the block data key that remembers which label each typed link was set for */
export const URL_LABEL_KEY = "__urlLbl";

export const ctaLabelNeedsLink = (label: unknown): boolean => NEEDS_OWN_LINK.test(String(label || ""));

function readUrlLabels(d: Record<string, unknown>): Record<string, string> {
  try { return JSON.parse(String(d[URL_LABEL_KEY] || "{}")) || {}; } catch { return {}; }
}

/** the label a typed link was set for, merged into the patch that claims the link */
export function urlLabelPatch(d: Record<string, unknown>, urlKey: string, label: unknown): Record<string, string> {
  const map = readUrlLabels(d);
  map[urlKey] = String(label || "");
  return { [URL_LABEL_KEY]: JSON.stringify(map) };
}

/** the same patch with this link's memory dropped — the link went back to the feed */
export function urlLabelRelease(d: Record<string, unknown>, urlKey: string): Record<string, string> {
  const map = readUrlLabels(d);
  if (!(urlKey in map)) return {};
  delete map[urlKey];
  return { [URL_LABEL_KEY]: JSON.stringify(map) };
}

/**
 * The one line under a CTA link box, or "" when the link and the label agree. Same
 * sentence feeds the Studio's warnings list, so the panel and the list never disagree.
 */
export function ctaLinkWarning(b: DynBlock, urlKey: string): string {
  const name = echNameOf(b.type);
  const labelKey = (ECH_CTA_PAIRS[name] || {})[urlKey];
  if (!labelKey) return "";
  const d = (b.data || {}) as Record<string, unknown>;
  const label = String(d[labelKey] ?? "").trim();
  if (!label) return "";
  if (socIsOff(b, urlKey)) {
    const was = readUrlLabels(d)[urlKey];
    if (was !== undefined && was.trim() !== label) {
      return "The label is now “" + label + "” — check this link still opens the right page.";
    }
    return "";
  }
  /* only when a page is STANDING IN for the right one — a blank or bare "https://" link is
     already the Done gate's (it asks for the URL); saying it twice is noise */
  const cur = String(d[urlKey] ?? "").trim();
  const standIn = !!cur && !/^https?:\/\/?$/i.test(cur);
  if (standIn && ctaLabelNeedsLink(label)) return "“" + label + "” needs its own link — paste the page it should open.";
  return "";
}

/** every flagged CTA link in the document, for the Studio's non-blocking warnings */
export function echCtaLinkWarnings(doc: DynBlock[]): { module: string; field: string; label: string; message: string }[] {
  const out: { module: string; field: string; label: string; message: string }[] = [];
  for (const b of doc) {
    const pairs = ECH_CTA_PAIRS[echNameOf(b.type)];
    if (!pairs) continue;
    for (const urlKey of Object.keys(pairs)) {
      const msg = ctaLinkWarning(b, urlKey);
      if (msg) out.push({ module: b.type, field: urlKey, label: "Button link", message: msg });
    }
  }
  return out;
}
